import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUploader from "@/components/ImageUploader";
import CategorySubcategoryPicker from "@/components/CategorySubcategoryPicker";
import { toast } from "sonner";
import { Lock, Sparkles, Plus, Gauge, Share2, Megaphone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = {
  title: "", description: "", category_id: "", subcategory_id: "", city: "",
  image: "", images: [], discount: "", advantage_type: "percentage", conditions: "", end_date: "",
  promo_code: "", max_redemptions: 0, coverage: "local",
};

const PRESETS = [
  { key: "resto", label: "Restaurant", data: { title: "Menu déjeuner à -20 %", discount: "-20%", description: "Profitez de -20 % sur notre menu déjeuner complet (entrée + plat + dessert), du lundi au vendredi. Produits frais et locaux, sur place ou à emporter.", conditions: "Valable du lundi au vendredi, hors jours fériés. Non cumulable avec d'autres promotions.", promo_code: "DEJ20" } },
  { key: "coiffeur", label: "Coiffeur / Beauté", data: { title: "-30 % sur votre 1re visite", discount: "-30%", description: "Nouveau client ? Bénéficiez de -30 % sur votre première prestation (coupe, couleur ou soin). Prenez rendez-vous et laissez-vous chouchouter près de chez vous.", conditions: "Réservé aux nouveaux clients, sur rendez-vous. Une seule utilisation par personne.", promo_code: "BIENVENUE30" } },
  { key: "artisan", label: "Artisan", data: { title: "Devis offert + 10 % de remise", discount: "-10%", description: "Un projet de rénovation ou de réparation ? Votre devis est offert et vous bénéficiez de -10 % sur les travaux. Intervention rapide et soignée dans tout le secteur.", conditions: "Remise sur présentation de ce bon, hors fournitures. Devis sans engagement.", promo_code: "TRAVAUX10" } },
  { key: "boutique", label: "Boutique", data: { title: "2 articles achetés = 1 offert", discount: "2+1 offert", description: "Pour 2 articles achetés, le 3e (le moins cher) vous est offert ! Venez découvrir nos nouveautés et faites-vous plaisir à prix doux.", conditions: "Offre valable en boutique sur les articles signalés. Le moins cher des trois est offert.", promo_code: "" } },
  { key: "bienetre", label: "Bien-être / Sport", data: { title: "1 mois d'essai à -50 %", discount: "-50%", description: "Testez nos cours et équipements pendant 1 mois à moitié prix. Coachs diplômés, ambiance conviviale et accompagnement personnalisé près de chez vous.", conditions: "Offre découverte pour les nouveaux inscrits, sans engagement au-delà du mois d'essai.", promo_code: "ESSAI50" } },
];

export default function ProOfferForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(empty);
  const [cities, setCities] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(false);
  const [createdOpen, setCreatedOpen] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.value ?? e }));

  const [showGuide, setShowGuide] = useState(() => { try { return localStorage.getItem("proOfferGuideDismissed") !== "1"; } catch { return true; } });
  const dismissGuide = () => { setShowGuide(false); try { localStorage.setItem("proOfferGuideDismissed", "1"); } catch {} };
  const fillExample = (preset) => {
    const d = preset.data;
    setForm((s) => ({ ...s,
      title: s.title || d.title,
      discount: s.discount || d.discount,
      description: s.description || d.description,
      conditions: s.conditions || d.conditions,
      promo_code: s.promo_code || d.promo_code,
    }));
    toast.success(`Exemple « ${preset.label} » pré-rempli : personnalisez-le puis publiez !`);
  };

  const [kw, setKw] = useState({ list: [], input: "" });
  const [genKw, setGenKw] = useState(false);
  const [genDesc, setGenDesc] = useState(false);
  const selectedKeywords = () => kw.list.filter((k) => k.on).map((k) => k.text);

  const generateKeywords = async () => {
    setGenKw(true);
    try {
      const { data } = await api.post("/pro/ai/offer-keywords", { title: form.title, discount: form.discount, city: form.city, category_id: form.category_id });
      const list = (data.keywords || []).map((t) => ({ text: t, on: true }));
      setKw((k) => ({ ...k, list }));
      if (!list.length) toast.info("Aucun mot-clé généré, réessayez.");
      else toast.success(`${list.length} mots-clés générés et pré-sélectionnés — décochez ceux à exclure, puis générez la description.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération IA indisponible."); }
    finally { setGenKw(false); }
  };
  const toggleKw = (i) => setKw((k) => ({ ...k, list: k.list.map((x, j) => (j === i ? { ...x, on: !x.on } : x)) }));
  const addKw = () => { const t = kw.input.trim(); if (!t) return; if (kw.list.some((x) => x.text.toLowerCase() === t.toLowerCase())) { setKw((k) => ({ ...k, input: "" })); return; } setKw((k) => ({ ...k, list: [...k.list, { text: t, on: true }], input: "" })); };
  const generateDescription = async () => {
    setGenDesc(true);
    try {
      const { data } = await api.post("/pro/ai/offer-description", { title: form.title, discount: form.discount, city: form.city, category_id: form.category_id, keywords: selectedKeywords() });
      if (data.description) { setForm((s) => ({ ...s, description: data.description })); toast.success("Description générée en intégrant vos mots-clés — adaptez-la."); }
      else toast.info("Aucune description générée, réessayez.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération IA indisponible."); }
    finally { setGenDesc(false); }
  };

  const offerSeo = useMemo(() => {
    const checks = [
      { ok: !!(form.title || "").trim(), w: 15 },
      { ok: (form.description || "").trim().length >= 80, w: 25 },
      { ok: !!(form.discount || "").trim(), w: 15 },
      { ok: !!form.category_id, w: 10 },
      { ok: !!(form.city || "").trim(), w: 10 },
      { ok: !!(form.image || (form.images || []).length), w: 20 },
      { ok: !!(form.conditions || "").trim(), w: 5 },
    ];
    const score = checks.reduce((s, c) => s + (c.ok ? c.w : 0), 0);
    let label = "À compléter", color = "#EF4444";
    if (score >= 85) { label = "Excellent"; color = "#16A34A"; }
    else if (score >= 65) { label = "Bon"; color = "#65A30D"; }
    else if (score >= 40) { label = "Correct"; color = "#F59E0B"; }
    return { score, label, color };
  }, [form]);

  const offerGp = useMemo(() => {
    const origin = (typeof window !== "undefined" && window.location?.origin) || "https://proxireduc-28.fr";
    const title = `${form.discount ? form.discount + " · " : ""}${(form.title || "").trim() || "Votre offre"} | PROXIreduc-28`;
    const slug = ((form.title || "offre").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")) || "offre";
    const crumb = `${origin.replace(/^https?:\/\//, "")} › offre › ${slug}`;
    const d = (form.description || "").trim();
    const desc = d ? (d.slice(0, 160) + (d.length > 160 ? "…" : "")) : `Bon plan local${form.city ? ` à ${form.city}` : " en Eure-et-Loir"} sur PROXIreduc-28.`;
    return { title, crumb, desc };
  }, [form]);

  const locked = quota && quota.limit === 0 && !isEdit;

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/pro/offers/quota").then((r) => setQuota(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/pro/offers/${id}`).then((r) => {
        const o = r.data;
        setForm({
          title: o.title || "", description: o.description || "",
          category_id: o.category_id || "", subcategory_id: o.subcategory_id || "", city: o.city || "",
          image: o.image || "",
          images: Array.isArray(o.images) && o.images.length ? o.images : (o.image ? [o.image] : []),
          discount: o.discount || "",
          advantage_type: o.advantage_type || "percentage",
          conditions: o.conditions || "",
          end_date: o.end_date || "",
          promo_code: o.promo_code || "", max_redemptions: o.max_redemptions || 0,
          coverage: o.coverage || "local",
        });
      }).catch(() => { toast.error("Offre introuvable"); navigate("/pro/offres"); });
    }
  }, [id, isEdit, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image && !(form.images || []).length) {
      toast.error("Ajoutez au moins une photo à votre offre.");
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/pro/offers/${id}`, form);
        toast.success("Promo mise à jour — soumise à validation");
        navigate("/pro/offres");
      } else {
        await api.post("/pro/offers", form);
        toast.success("Promo soumise à validation 🎉");
        setCreatedOpen(true);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur d'enregistrement"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold" data-testid="pro-offer-form-title">
        {isEdit ? "Modifier la promo" : "Nouvelle promo / bon plan"}
      </h1>
      {isEdit && (
        <p className="text-sm text-slate-500 mt-1">
          Toute modification repasse par la validation de l'administrateur.
        </p>
      )}
      {!isEdit && (
        <div className="mt-3 max-w-3xl rounded-2xl bg-gradient-to-br from-[#E05638] to-[#c94a2f] text-white p-5" data-testid="pro-offer-why">
          <h2 className="font-heading font-extrabold text-lg flex items-center gap-2"><Megaphone size={18}/> Pourquoi publier une promo / un bon plan ?</h2>
          <p className="mt-1.5 text-sm text-white/90">
            Une remise ou un bon plan (ex. <b>−10 % sur la nouvelle collection</b>) attire les visiteurs et les membres de PROXIreduc-28 directement vers votre commerce. C'est le moyen le plus efficace de vous faire connaître localement, de déclencher une première visite et de fidéliser de nouveaux clients — votre promo apparaît sur votre mini-site, sur PROXIreduc et peut être partagée sur les réseaux.
          </p>
        </div>
      )}
      {!isEdit && !locked && showGuide && (
        <div className="mt-4 max-w-3xl rounded-2xl border border-orange-200 bg-orange-50/70 p-5" data-testid="pro-offer-guide">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Sparkles size={18} className="text-[#E05638]"/> Publier votre promo en 3 étapes</h2>
            <button type="button" onClick={dismissGuide} className="text-slate-400 hover:text-slate-600 text-lg leading-none" data-testid="pro-offer-guide-dismiss" aria-label="Masquer le guide">✕</button>
          </div>
          <ol className="mt-3 space-y-2 text-sm text-slate-700">
            {[
              <>Donnez un <b>titre clair</b> et indiquez la <b>réduction</b> (ex. « -20 % », « 2+1 offert »).</>,
              <>Ajoutez une <b>photo</b> attrayante et une courte <b>description</b> (l'IA peut la rédiger pour vous).</>,
              <>Choisissez la <b>zone</b> et la <b>date de fin</b>, puis <b>publiez</b> : votre promo apparaît sur votre mini-site et sur PROXIreduc, et vous pourrez la publier sur les réseaux et groupes.</>,
            ].map((txt, i) => (
              <li key={i} className="flex gap-2.5 items-start">
                <span className="shrink-0 w-5 h-5 rounded-full bg-[#E05638] text-white text-[11px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                <span>{txt}</span>
              </li>
            ))}
          </ol>
          <div className="mt-4">
            <div className="text-xs font-semibold text-slate-600 mb-1.5">Pré-remplir un exemple selon votre métier :</div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <Button key={p.key} type="button" size="sm" variant="outline" onClick={() => fillExample(p)} className="border-orange-200 hover:bg-orange-100" data-testid={`pro-offer-example-${p.key}`}>{p.label}</Button>
              ))}
              <Button type="button" size="sm" variant="ghost" onClick={dismissGuide} data-testid="pro-offer-guide-got-it">J'ai compris</Button>
            </div>
          </div>
        </div>
      )}
      {locked ? (
        <div className="mt-6 max-w-3xl rounded-2xl border border-amber-300 bg-amber-50 p-6 text-center" data-testid="pro-offer-form-locked">
          <Lock className="mx-auto text-amber-600" size={28}/>
          <h2 className="mt-3 font-heading text-xl font-extrabold text-slate-900">Réservé aux formules payantes</h2>
          <p className="mt-2 text-sm text-slate-700 max-w-xl mx-auto">
            La création d'offres n'est pas incluse dans votre formule <b>Présence (gratuite)</b>.
            Passez à <b>Essentiel</b> (1 offre), <b>Visibilité</b> (5 offres) ou <b>Premium</b> (offres illimitées)
            pour publier vos promotions et attirer de nouveaux clients près de chez vous.
          </p>
          <div className="mt-5 flex items-center justify-center gap-3">
            <Link to="/pro/abonnement"><Button className="btn-shine text-white" data-testid="pro-offer-form-upgrade-btn">Voir les formules payantes</Button></Link>
            <Button variant="outline" onClick={()=>navigate("/pro/offres")}>Retour</Button>
          </div>
        </div>
      ) : (
      <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 space-y-4 max-w-3xl" data-testid="pro-offer-form">
        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3" data-testid="offer-seo-score">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><Gauge size={14} className="text-[#E05638]"/> Optimisation de l'offre</div>
            <div className="text-xs font-bold" style={{ color: offerSeo.color }} data-testid="offer-seo-value">{offerSeo.score}/100 · {offerSeo.label}</div>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-slate-200 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${offerSeo.score}%`, background: offerSeo.color }} />
          </div>
        </div>
        <div>
          <Label>Zone de couverture</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <button type="button" onClick={()=>setForm((s)=>({...s, coverage: "local"}))}
                    data-testid="coverage-local-btn"
                    className={`p-3 rounded-xl border text-left text-sm ${form.coverage==="local"?"border-[#E05638] bg-orange-50":"border-slate-200 bg-white"}`}>
              <div className="font-semibold">Ma ville / mon pôle</div>
              <div className="text-xs text-slate-500">Offre visible pour cette commune</div>
            </button>
            <button type="button" onClick={()=>setForm((s)=>({...s, coverage: "department"}))}
                    data-testid="coverage-department-btn"
                    className={`p-3 rounded-xl border text-left text-sm ${form.coverage==="department"?"border-[#E05638] bg-orange-50":"border-slate-200 bg-white"}`}>
              <div className="font-semibold">Tout l'Eure-et-Loir</div>
              <div className="text-xs text-slate-500">Idéal si vous intervenez dans tout le département (plombier, alarme, artisan mobile…)</div>
            </button>
          </div>
        </div>
        <div><Label>Titre</Label><Input required value={form.title} onChange={set("title")} data-testid="offer-title-input"/></div>
        <div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Label>Description</Label>
            <div className="flex items-center gap-2">
              <Button type="button" size="sm" variant="outline" onClick={generateKeywords} disabled={genKw}
                      className="h-8 text-[#E05638] border-orange-200 hover:bg-orange-50" data-testid="offer-ai-keywords">
                <Sparkles size={13} className={`mr-1 ${genKw ? "animate-pulse" : ""}`}/> {genKw ? "Génération…" : "1. 20 mots-clés (IA)"}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={generateDescription} disabled={genDesc}
                      className="h-8 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="offer-ai-description">
                <Sparkles size={13} className={`mr-1 ${genDesc ? "animate-pulse" : ""}`}/> {genDesc ? "Rédaction…" : "2. Générer la description (IA)"}
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mt-1 mb-2 leading-relaxed bg-orange-50/60 border border-orange-100 rounded-lg px-3 py-2" data-testid="offer-ai-help">
            <span className="font-semibold text-slate-700">Astuce référencement :</span> générez d'abord les <strong>mots-clés</strong> de votre offre (activité, ville, département). Ils sont tous <strong>pré-sélectionnés</strong> : décochez ceux que vous ne voulez pas, ajoutez les vôtres, puis cliquez sur <strong>Générer la description</strong> pour un <strong>référencement optimal sur Google</strong>.
          </p>
          {kw.list.length > 0 && (
            <div className="mt-2 mb-2 rounded-lg border border-orange-100 bg-orange-50/40 p-3" data-testid="offer-keywords">
              <p className="text-[11px] text-slate-500 mb-1.5">Mots-clés qui seront intégrés à la description — cliquez pour (dé)sélectionner, ou ajoutez les vôtres :</p>
              <div className="flex flex-wrap gap-1.5">
                {kw.list.map((k, i) => (
                  <button key={i} type="button" onClick={() => toggleKw(i)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${k.on ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-500 border-slate-300 hover:border-slate-400"}`}
                    data-testid={`offer-kw-${i}`}>{k.text}</button>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Input value={kw.input} onChange={(e) => setKw((k) => ({ ...k, input: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKw(); } }} placeholder="Ajouter un mot-clé…" className="max-w-xs h-8" data-testid="offer-kw-input" />
                <Button type="button" variant="outline" size="sm" onClick={addKw} data-testid="offer-kw-add"><Plus size={14} className="mr-1" /> Ajouter</Button>
                <span className="text-xs text-slate-400">{kw.list.filter((k) => k.on).length} sélectionné(s)</span>
              </div>
            </div>
          )}
          <Textarea rows={4} value={form.description} onChange={set("description")} data-testid="offer-desc-input"/>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="offer-google-preview">
          <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Aperçu dans Google</div>
          <div className="max-w-xl">
            <div className="text-xs text-slate-600 truncate" style={{ fontFamily: "arial,sans-serif" }}>{offerGp.crumb}</div>
            <div className="text-[#1a0dab] text-lg leading-snug truncate" style={{ fontFamily: "arial,sans-serif" }}>{offerGp.title}</div>
            <div className="text-sm text-slate-600 mt-0.5 leading-snug" style={{ fontFamily: "arial,sans-serif" }}>{offerGp.desc}</div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Simulation indicative : un titre clair, une réduction visible et une description riche en mots-clés améliorent votre visibilité Google.</p>
        </div>
        <CategorySubcategoryPicker
          value={{ category_id: form.category_id, subcategory_id: form.subcategory_id }}
          onChange={({ category_id, subcategory_id }) =>
            setForm((s) => ({ ...s, category_id, subcategory_id }))}
          testIdPrefix="offer"
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Ville</Label>
            <Select value={form.city} onValueChange={(v)=>setForm((s)=>({...s, city: v}))}>
              <SelectTrigger data-testid="offer-city-select"><SelectValue placeholder="Choisir"/></SelectTrigger>
              <SelectContent>{cities.map((c)=>(<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div><Label>Réduction (ex: -25%)</Label><Input required value={form.discount} onChange={set("discount")} data-testid="offer-discount-input"/></div>
          <div>
            <Label>Type d'avantage</Label>
            <Select value={form.advantage_type} onValueChange={(v)=>setForm((s)=>({...s, advantage_type: v}))}>
              <SelectTrigger data-testid="offer-adv-type"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Pourcentage</SelectItem>
                <SelectItem value="fixed">Réduction fixe</SelectItem>
                <SelectItem value="formula">Formule</SelectItem>
                <SelectItem value="gift">Cadeau</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Conditions</Label><Textarea rows={3} value={form.conditions} onChange={set("conditions")}/></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Date de fin</Label>
            <Input type="date" data-testid="pro-offer-end-date"
                   value={form.end_date ? form.end_date.slice(0, 10) : ""}
                   onChange={(e)=>setForm((s)=>({...s, end_date: e.target.value ? new Date(e.target.value + "T23:59:59").toISOString() : ""}))}/>
          </div>
          <div><Label>Code promo (optionnel)</Label><Input value={form.promo_code} onChange={set("promo_code")}/></div>
        </div>
        <div>
          <Label>Images de l'offre (jusqu'à 5) <span className="text-[#E05638]">*</span></Label>
          <p className="text-xs text-slate-500 mt-0.5">Au moins une photo est obligatoire pour publier l'offre.</p>
          <div className="mt-2">
            <ImageUploader
              value={form.images}
              onChange={(imgs)=>setForm((s)=>({...s, images: imgs, image: imgs[0] || ""}))}
            />
          </div>
        </div>
        <div className="flex gap-3">
          <Button type="submit" disabled={loading || (!form.image && !(form.images||[]).length)} className="btn-shine text-white" data-testid="offer-submit-btn">
            {loading ? "Enregistrement…" : (isEdit ? "Enregistrer les modifications" : "Soumettre pour validation")}
          </Button>
          <Button type="button" variant="outline" onClick={()=>navigate("/pro/offres")}>Annuler</Button>
        </div>
      </form>
      )}

      <Dialog open={createdOpen} onOpenChange={(o) => { if (!o) { setCreatedOpen(false); navigate("/pro/offres"); } }}>
        <DialogContent data-testid="pro-offer-created-dialog">
          <DialogHeader><DialogTitle>🎉 Votre promo est créée !</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Elle sera visible après validation. Pour lui donner un maximum de visibilité, partagez-la dès maintenant sur Facebook et dans les groupes locaux.
          </p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => { setCreatedOpen(false); navigate("/pro/offres"); }} data-testid="pro-offer-created-later">
              Voir mes promotions
            </Button>
            <Button onClick={() => { setCreatedOpen(false); navigate("/pro/publications-facebook"); }} className="btn-shine text-white" data-testid="pro-offer-created-share">
              <Share2 size={14} className="mr-1.5"/> Partager sur Facebook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
